import type { ReactNode } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { OverlayScrollArea } from '@odo/shared-ui';
import { dmInputClass } from '../DmControlPrimitives';
import type { ExcludedTeethCategory } from './excludedTeethTaxonomy';

export const OVERVIEW = 'overview';

interface NavItemProps {
    active: boolean;
    count: number;
    onClick: () => void;
    children: ReactNode;
}

function NavItem({ active, count, onClick, children }: NavItemProps) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-current={active ? 'true' : undefined}
            className={`flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-[13px] ${
                active ? 'bg-gray-100 font-medium text-gray-800' : 'text-gray-700 hover:bg-gray-50'
            }`}
        >
            <span className="truncate">{children}</span>
            {count > 0 && (
                <span className="text-xs font-semibold tabular-nums text-[#007E87]">{count}</span>
            )}
        </button>
    );
}

interface ExcludedTeethSidebarProps {
    categories: readonly ExcludedTeethCategory[];
    selectedId: string;
    onSelect: (id: string) => void;
    search: string;
    onSearch: (value: string) => void;
    /** Notifications with teeth, per observation id, in the current draft. */
    counts: ReadonlyMap<string, number>;
    overviewCount: number;
}

export function ExcludedTeethSidebar({
    categories,
    selectedId,
    onSelect,
    search,
    onSearch,
    counts,
    overviewCount
}: ExcludedTeethSidebarProps) {
    const searching = search.trim() !== '';
    return (
        <aside className="flex w-64 shrink-0 flex-col border-r border-gray-200">
            <div className="relative shrink-0 pb-3 pl-6 pr-4 pt-4">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-[34px] top-6 h-4 w-4 text-gray-400" />
                <input
                    value={search}
                    onChange={(e) => onSearch(e.target.value)}
                    placeholder="Search notifications"
                    aria-label="Search notifications"
                    className={`${dmInputClass} w-full pl-8`}
                />
            </div>
            <OverlayScrollArea className="min-h-0 flex-1" contentClassName="pb-4 pl-6 pr-4">
                <nav className="space-y-4 pt-1" aria-label="Notification groups">
                    <NavItem
                        active={!searching && selectedId === OVERVIEW}
                        count={overviewCount}
                        onClick={() => onSelect(OVERVIEW)}
                    >
                        Overall view
                    </NavItem>
                    {categories.map((category) => (
                        <div key={category.id}>
                            <p className="px-2 pb-1 text-xs font-semibold text-gray-500">
                                {category.label}
                            </p>
                            {category.observations.map((observation) => (
                                <NavItem
                                    key={observation.id}
                                    active={!searching && selectedId === observation.id}
                                    count={counts.get(observation.id) ?? 0}
                                    onClick={() => onSelect(observation.id)}
                                >
                                    {observation.label}
                                </NavItem>
                            ))}
                        </div>
                    ))}
                </nav>
            </OverlayScrollArea>
        </aside>
    );
}
